import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useLatest } from 'react-use';

import { useUndoRedoState } from '@/components/diagrams/FlowChart/hooks/useUndoRedoState';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { type HouseholdChatMessage } from '@/modules/household/chat/contexts/householdChat.context';
import { HouseholdChatProvider } from '@/modules/household/chat/contexts/HouseholdChat.provider';
import { useGeneratingDiagramArtifactIds } from '@/modules/workflowTrackers/hooks/useGeneratingDiagramArtifactIds';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { type WorkflowTracker, WorkflowTrackerKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { useRenameBuilderDiagramMutation } from '../graphql/DiagramArtifactName.generated';
import {
  useBuilderSavedDiagramQuery,
  useUpdateBuilderDiagramMutation,
} from '../graphql/DiagramPersistence.generated';
import { diagramInput, isSameDiagram } from '../utils/diagram';
import { isDiagramMessageForArtifact } from '../utils/diagramMessage';
import { DiagramEditorContext } from './diagramEditor.context';

interface DiagramEditorProviderProps {
  householdId: string;
  /** The saved artifact to edit. Null for a new diagram. */
  artifactID: string | null;
  onClose: () => void;
}

function useContextValue({
  householdId,
  artifactID,
  onClose,
}: DiagramEditorProviderProps) {
  const {
    data,
    loading,
    error: loadError,
    refetch,
  } = useBuilderSavedDiagramQuery({
    variables: { id: artifactID ?? '' },
    skip: !artifactID,
    fetchPolicy: 'network-only',
  });
  const { reportError } = useReportError(
    'Failed to load the diagram',
    loadError
  );
  const { showFeedback } = useFeedback();
  const artifact =
    data?.node?.__typename === 'DiagramArtifact' ? data.node : null;
  const saved = artifact?.diagram ?? null;
  const conversationID =
    getNodes(artifact?.latestGeneration)[0]?.conversation?.id ?? null;

  const { present, setState, undo, redo, canUndo, canRedo } =
    useUndoRedoState<DiagramBuilder_DiagramFragment | null>(() => null);
  const latestPresent = useLatest(present);
  // Show the saved diagram when it loads and when a generation changes it.
  // Undo restores the editor diagram from before the change.
  useEffect(() => {
    if (!saved || isSameDiagram(latestPresent.current, saved)) return;
    setState(saved, { commit: latestPresent.current !== null });
  }, [latestPresent, saved, setState]);

  const generatingArtifactIDs = useGeneratingDiagramArtifactIds();
  const isGenerating = !!artifactID && generatingArtifactIDs.has(artifactID);
  const isGenerationOfThisDiagram = useCallback(
    (tracker: WorkflowTracker) => tracker.diagramArtifact?.id === artifactID,
    [artifactID]
  );
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DiagramGeneration,
    filter: isGenerationOfThisDiagram,
    onCompleted: () => {
      void refetch();
    },
    enabled: !!artifactID,
  });

  const [updateDiagram, { loading: isSaving }] =
    useUpdateBuilderDiagramMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isDirty = !!present && !!saved && !isSameDiagram(present, saved);

  async function save() {
    if (!artifactID || !artifact || !present) return false;
    setSaveError(null);
    try {
      await updateDiagram({
        variables: {
          input: {
            householdID: householdId,
            diagramArtifactID: artifactID,
            // The backend rejects the save if the diagram changed since it loaded.
            expectedRevision: artifact.revision,
            diagram: diagramInput(present),
          },
        },
      });
      await refetch();
      return true;
    } catch {
      setSaveError('The diagram could not be saved. Try again.');
      return false;
    }
  }

  const [renameDiagram, { loading: isRenaming }] =
    useRenameBuilderDiagramMutation();

  async function rename(name: string) {
    const trimmedName = name.trim();
    if (!artifactID || !trimmedName || trimmedName === artifact?.name) return;
    try {
      // The response has the artifact ID and name, so Apollo updates the
      // cached artifact. The header and the artifacts table show the new name.
      await renameDiagram({
        variables: {
          input: {
            householdID: householdId,
            diagramArtifactID: artifactID,
            name: trimmedName,
          },
        },
      });
    } catch (error) {
      reportError('Failed to rename the diagram', error as Error);
      showFeedback('The diagram name could not be saved. Try again.', {
        variant: 'error',
      });
    }
  }

  const value: DiagramEditorContext = {
    artifactID,
    diagram: present,
    name: artifact?.name ?? null,
    loading,
    isGenerating,
    isSaving,
    isRenaming,
    isDirty,
    error: saveError ?? (loadError ? 'The diagram could not be loaded.' : null),
    history: { canUndo, canRedo, onUndo: undo, onRedo: redo },
    update: (diagram) => setState(diagram),
    save,
    rename,
    close: onClose,
  };
  return { value, conversationID, revision: artifact?.revision ?? null };
}

/**
 * Provides the diagram editor state and the diagram's household chat. Each
 * chat send includes the diagram that the user edits, so the backend can
 * apply the request to it.
 */
export function DiagramEditorProvider({
  children,
  ...props
}: PropsWithChildren<DiagramEditorProviderProps>) {
  const { value, conversationID, revision } = useContextValue(props);
  const { artifactID, diagram, isDirty } = value;
  // The current diagram is the diagram that this editor shows. The editor
  // shows its updates live beside the chat, so the chat hides its
  // DIAGRAM_MESSAGE messages. DIAGRAM_MESSAGE messages for other diagrams stay
  // visible.
  const shouldHideCurrentDiagramMessage = useCallback(
    (message: HouseholdChatMessage) =>
      isDiagramMessageForArtifact(message, artifactID),
    [artifactID]
  );

  return (
    <DiagramEditorContext.Provider value={value}>
      <HouseholdChatProvider
        householdId={props.householdId}
        singleConversation={{ conversationID }}
        transcriptOptions={{ isMessageHidden: shouldHideCurrentDiagramMessage }}
        getAdditionalSendInput={() =>
          artifactID && revision !== null
            ? {
                diagramContext: {
                  diagramArtifactID: artifactID,
                  expectedRevision: revision,
                  // A clean diagram is read from the saved artifact.
                  diagram:
                    isDirty && diagram ? diagramInput(diagram) : undefined,
                },
              }
            : undefined
        }
      >
        {children}
      </HouseholdChatProvider>
    </DiagramEditorContext.Provider>
  );
}
